import { useState } from 'react';
import type {
  AttributeLibraryResponse,
  CapabilityLibraryResponse,
  ExperienceAreaLibraryResponse,
  LibraryDefinitionAdminResponse,
  QualificationLibraryResponse,
  RoleAdminResponse,
  RoleListResponse,
  RoleRequirementsResponse,
  SkillLibraryResponse,
} from '../../api/types';
import { apiPatch, apiPost, describeError } from '../../api/client';
import { useApiResource } from '../../hooks/useApiResource';
import { ErrorState, LoadingState } from '../StateMessage';
import { ActionFeedback, AdminForm, ConfirmAction, Field, StatusTag, useAction } from './adminShared';
import styles from './admin.module.css';

/**
 * The role library, and the requirements that give a role its meaning
 * (Mission 018).
 *
 * ---------------------------------------------------------------------
 * TWO LIFECYCLES, KEPT VISIBLY APART
 *
 * A Role's own fields are ordinary editable metadata: `docs/DOMAIN_MODEL.md`
 * §11 says the record "is edited only non-meaningfully in place", and
 * nothing derived reads a role's prose — Role Fit is computed entirely from
 * its requirements.
 *
 * A **requirement is never edited**. It is effective-dated history, and the
 * database enforces that with a trigger. Raising a threshold means ending
 * the current period and opening a new one, so this screen offers "End this
 * requirement" and "Add a requirement" and no edit control at all. Calling
 * that pair "editing a requirement" would hide the fact that the old
 * threshold remains true of the period it covered — which is exactly what
 * makes a past Role Fit result still explicable.
 * ---------------------------------------------------------------------
 */
export function RoleLibrarySection() {
  const roles = useApiResource<RoleListResponse>('/roles');
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <section className={styles.section} aria-label="Role library">
      <h2 className={styles.sectionTitle}>Role library</h2>
      <p className={styles.sectionNote}>
        The roles people hold and are compared against. A retired role stays listed, because the role assignments and
        Role Fit results that reference it still need to make sense.
      </p>

      {roles.status === 'loading' ? <LoadingState label="Loading roles…" /> : null}
      {roles.status === 'failed' ? (
        <ErrorState title="The role library could not be loaded" detail={describeError(roles.error)} />
      ) : null}

      {roles.status === 'loaded' ? (
        <>
          <ul className={styles.list}>
            {roles.data.roles.map((role) => (
              <li key={role.id} className={styles.row}>
                <span className={styles.rowName}>{role.name}</span>
                <StatusTag status={role.status} />
                <span className={styles.rowMeta}>
                  {role.levelLabel ?? 'No level'}
                  {role.departmentName ? ` · ${role.departmentName}` : ''}
                </span>
                <span className={styles.rowActions}>
                  {/* "Manage", not "Edit" (unlike every other library section): this panel
                      also opens Requirements — a separate effective-dated sub-lifecycle,
                      not just field-editing — so "Edit" would undersell what the button
                      does. */}
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={() => setEditing(editing === role.id ? null : role.id)}
                  >
                    {editing === role.id ? 'Close' : 'Manage'}
                  </button>
                </span>
                {editing === role.id ? (
                  <RoleEditor
                    role={role as RoleAdminResponse}
                    onDone={() => {
                      setEditing(null);
                      roles.reload();
                    }}
                  />
                ) : null}
              </li>
            ))}
          </ul>

          {creating ? (
            <RoleCreator
              onDone={() => {
                setCreating(false);
                roles.reload();
              }}
            />
          ) : (
            <div className={styles.actions}>
              <button type="button" className={styles.primaryButton} onClick={() => setCreating(true)}>
                Define a new role
              </button>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}

function RoleCreator({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [levelLabel, setLevelLabel] = useState('');
  const { state, run } = useAction();

  async function submit() {
    const ok = await run(async () => {
      await apiPost('/admin/roles', { name, levelLabel: levelLabel || null });
      return `"${name}" was added to the role library.`;
    });
    if (ok) onDone();
  }

  return (
    <AdminForm title="Define a new role" onSubmit={submit} submitLabel="Create role" busy={state.status === 'saving'}>
      <Field id="new-role-name" label="Name" value={name} onChange={setName} />
      <Field
        id="new-role-level"
        label="Level label"
        value={levelLabel}
        onChange={setLevelLabel}
        hint="Optional. Two roles may share a name at different levels."
      />
      <ActionFeedback state={state} />
    </AdminForm>
  );
}

function RoleEditor({ role, onDone }: { role: RoleAdminResponse; onDone: () => void }) {
  const [name, setName] = useState(role.name);
  const [responsibilities, setResponsibilities] = useState(role.responsibilities ?? '');
  const { state, run } = useAction();

  async function save() {
    const ok = await run(async () => {
      await apiPatch(`/admin/roles/${role.id}`, { name, responsibilities: responsibilities || null });
      return `"${name}" was updated.`;
    });
    if (ok) onDone();
  }

  async function retire() {
    const ok = await run(async () => {
      await apiPost(`/admin/roles/${role.id}/retirement`, {});
      return `"${role.name}" was retired. It stays in the library, labelled.`;
    });
    if (ok) onDone();
  }

  async function reactivate() {
    const ok = await run(async () => {
      await apiPost(`/admin/roles/${role.id}/reactivation`, {});
      return `"${role.name}" was reactivated.`;
    });
    if (ok) onDone();
  }

  return (
    <>
      <AdminForm title={`Edit ${role.name}`} onSubmit={save} submitLabel="Save changes" busy={state.status === 'saving'}>
        <Field id={`role-name-${role.id}`} label="Name" value={name} onChange={setName} />
        <Field
          id={`role-resp-${role.id}`}
          label="Responsibilities"
          value={responsibilities}
          onChange={setResponsibilities}
          multiline
          hint="Prose only. What a role demands is its requirements, below — nothing derived reads this field."
        />
        <ActionFeedback state={state} />

        {role.status === 'active' ? (
          <ConfirmAction
            label="Retire this role"
            confirmLabel="Retire it"
            busy={state.status === 'saving'}
            description={
              <>
                Retiring <strong>{role.name}</strong> takes it out of use for new assignments. It is{' '}
                <strong>not</strong> deleted: it stays in the library marked retired, because the role assignments and
                Role Fit results that reference it still need to make sense.
              </>
            }
            onConfirm={retire}
          />
        ) : (
          <>
            <p className={styles.locked}>This role is retired and stays listed for the history that references it.</p>
            <div className={styles.actions}>
              <button type="button" className={styles.secondaryButton} onClick={reactivate} disabled={state.status === 'saving'}>
                {state.status === 'saving' ? 'Working…' : 'Reactivate this role'}
              </button>
            </div>
          </>
        )}
      </AdminForm>

      <RoleRequirements roleId={role.id} roleName={role.name} />
    </>
  );
}

/**
 * A role's requirements — opened and ended, never edited.
 *
 * All five subject types the API accepts (attribute, skill, qualification,
 * experience area, capability) have a picker here — Admin Library-Listing
 * Endpoints (Product Owner decision, 2026-09-19) completed the first four;
 * Capability's own admin library (Mission 166) completed the fifth,
 * closing the gap `docs/DOMAIN_DECISIONS.md`'s "Composite Concepts"
 * (Mission 165) Part 2E itself named.
 */
function RoleRequirements({ roleId, roleName }: { roleId: string; roleName: string }) {
  const requirements = useApiResource<RoleRequirementsResponse>(`/roles/${roleId}/requirements`);
  const attributes = useApiResource<AttributeLibraryResponse>('/admin/attribute-definitions');
  const skills = useApiResource<SkillLibraryResponse>('/admin/skills');
  const qualifications = useApiResource<QualificationLibraryResponse>('/admin/qualifications');
  const experienceAreas = useApiResource<ExperienceAreaLibraryResponse>('/admin/experience-areas');
  const capabilities = useApiResource<CapabilityLibraryResponse>('/admin/capabilities');
  const [adding, setAdding] = useState(false);
  const { state, run } = useAction();

  async function close(requirementId: string) {
    await run(async () => {
      const today = new Date().toISOString().slice(0, 10);
      await apiPost(`/admin/roles/${roleId}/requirements/${requirementId}/closure`, { effectiveTo: today });
      requirements.reload();
      return 'That requirement period was ended. It stays on record for the period it covered.';
    });
  }

  return (
    <div className={styles.form}>
      <h3 className={styles.formTitle}>What {roleName} requires</h3>
      <p className={styles.hint}>
        Requirements are dated periods, not settings. To change a threshold, end the current period and add a new one —
        the old threshold stays true of the time it covered, which is what keeps past Role Fit results explicable.
      </p>

      {requirements.status === 'loading' ? <LoadingState label="Loading requirements…" /> : null}
      {requirements.status === 'failed' ? (
        <ErrorState title="Requirements could not be loaded" detail={describeError(requirements.error)} />
      ) : null}

      {requirements.status === 'loaded' ? (
        requirements.data.requirements.length === 0 ? (
          <p className={styles.hint}>This role has no current requirements, so no Role Fit can be computed for it.</p>
        ) : (
          <ul className={styles.list}>
            {requirements.data.requirements.map((requirement) => (
              <li key={requirement.id} className={styles.row}>
                <span className={styles.rowName}>{requirement.subjectName}</span>
                <span className={styles.rowMeta}>
                  {requirement.requirementLevel}
                  {requirement.thresholdRating !== null ? ` · at least ${requirement.thresholdRating} of 20` : ''}
                  {requirement.thresholdProficiencyLevel ? ` · ${requirement.thresholdProficiencyLevel}` : ''}
                  {requirement.thresholdMinYears !== null ? ` · ${requirement.thresholdMinYears} years` : ''}
                </span>
                <span className={styles.rowActions}>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    disabled={state.status === 'saving'}
                    onClick={() => close(requirement.id)}
                  >
                    End this requirement
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )
      ) : null}

      <ActionFeedback state={state} />

      {adding &&
      attributes.status === 'loaded' &&
      skills.status === 'loaded' &&
      qualifications.status === 'loaded' &&
      experienceAreas.status === 'loaded' &&
      capabilities.status === 'loaded' ? (
        <RequirementCreator
          roleId={roleId}
          attributes={attributes.data.attributes.filter((a) => a.status === 'active')}
          skills={skills.data.skills.filter((s) => s.status === 'active')}
          qualifications={qualifications.data.qualifications.filter((q) => q.status === 'active')}
          experienceAreas={experienceAreas.data.experienceAreas.filter((e) => e.status === 'active')}
          capabilities={capabilities.data.capabilities.filter((c) => c.status === 'active')}
          onDone={() => {
            setAdding(false);
            requirements.reload();
          }}
        />
      ) : adding ? (
        <LoadingState label="Loading the requirement libraries…" />
      ) : (
        <div className={styles.actions}>
          <button type="button" className={styles.secondaryButton} onClick={() => setAdding(true)}>
            Add a requirement
          </button>
        </div>
      )}
    </div>
  );
}

type RequirementSubjectType = 'attribute' | 'skill' | 'qualification' | 'experience_area' | 'capability';

const SUBJECT_TYPE_LABEL: Record<RequirementSubjectType, string> = {
  attribute: 'Attribute',
  skill: 'Skill',
  qualification: 'Qualification',
  experience_area: 'Experience area',
  capability: 'Capability',
};

/** Admin Library-Listing Endpoints (Product Owner decision, 2026-09-19),
 * completed by Capability's own admin library (Mission 166). Each subject
 * type keeps its own threshold shape — a rating means nothing for a
 * qualification, a proficiency level means nothing for an attribute — so
 * the type selection decides which library and which threshold field this
 * form shows. Capability shares Attribute's `thresholdRating` field: a
 * computed Capability value is rescaled onto the same 1-20 scale
 * specifically so it can be compared the same way (Mission 166's
 * `computeCapabilityValue`). */
function RequirementCreator({
  roleId,
  attributes,
  skills,
  qualifications,
  experienceAreas,
  capabilities,
  onDone,
}: {
  roleId: string;
  attributes: AttributeLibraryResponse['attributes'];
  skills: LibraryDefinitionAdminResponse[];
  qualifications: LibraryDefinitionAdminResponse[];
  experienceAreas: LibraryDefinitionAdminResponse[];
  capabilities: { id: string; name: string }[];
  onDone: () => void;
}) {
  const [subjectType, setSubjectType] = useState<RequirementSubjectType>('attribute');
  const subjectsByType: Record<RequirementSubjectType, { id: string; name: string }[]> = {
    attribute: attributes,
    skill: skills,
    qualification: qualifications,
    experience_area: experienceAreas,
    capability: capabilities,
  };
  const subjects = subjectsByType[subjectType];

  const [subjectId, setSubjectId] = useState(attributes[0]?.id ?? '');
  const [requirementLevel, setRequirementLevel] = useState<'required' | 'preferred'>('required');
  const [thresholdRating, setThresholdRating] = useState('14');
  const [thresholdProficiencyLevel, setThresholdProficiencyLevel] = useState<
    'beginner' | 'intermediate' | 'advanced' | 'expert'
  >('intermediate');
  const [thresholdMinYears, setThresholdMinYears] = useState('2');
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  const { state, run } = useAction();

  function changeSubjectType(next: RequirementSubjectType) {
    setSubjectType(next);
    setSubjectId(subjectsByType[next][0]?.id ?? '');
  }

  async function submit() {
    const ok = await run(async () => {
      await apiPost(`/admin/roles/${roleId}/requirements`, {
        subjectType,
        subjectId,
        requirementLevel,
        thresholdRating: subjectType === 'attribute' || subjectType === 'capability' ? Number(thresholdRating) : undefined,
        thresholdProficiencyLevel: subjectType === 'skill' ? thresholdProficiencyLevel : undefined,
        thresholdMinYears: subjectType === 'experience_area' ? Number(thresholdMinYears) : undefined,
        effectiveFrom,
      });
      return 'The requirement period was opened.';
    });
    if (ok) onDone();
  }

  return (
    <AdminForm title="Add a requirement" onSubmit={submit} submitLabel="Open this requirement" busy={state.status === 'saving'}>
      <div className={styles.field}>
        <label className={styles.label} htmlFor={`req-type-${roleId}`}>
          Requirement type
        </label>
        <select
          id={`req-type-${roleId}`}
          className={styles.input}
          value={subjectType}
          onChange={(event) => changeSubjectType(event.target.value as RequirementSubjectType)}
        >
          {(Object.keys(SUBJECT_TYPE_LABEL) as RequirementSubjectType[]).map((type) => (
            <option key={type} value={type}>
              {SUBJECT_TYPE_LABEL[type]}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor={`req-subject-${roleId}`}>
          {SUBJECT_TYPE_LABEL[subjectType]}
        </label>
        {subjects.length === 0 ? (
          <p className={styles.hint}>No active {SUBJECT_TYPE_LABEL[subjectType].toLowerCase()} definitions exist.</p>
        ) : (
          <select
            id={`req-subject-${roleId}`}
            className={styles.input}
            value={subjectId}
            onChange={(event) => setSubjectId(event.target.value)}
          >
            {subjects.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {subject.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor={`req-level-${roleId}`}>
          Requirement level
        </label>
        <select
          id={`req-level-${roleId}`}
          className={styles.input}
          value={requirementLevel}
          onChange={(event) => setRequirementLevel(event.target.value as 'required' | 'preferred')}
        >
          <option value="required">Required</option>
          <option value="preferred">Preferred</option>
        </select>
      </div>

      {subjectType === 'attribute' || subjectType === 'capability' ? (
        <Field
          id={`req-threshold-${roleId}`}
          label="Minimum rating (1–20)"
          value={thresholdRating}
          onChange={setThresholdRating}
          hint={subjectType === 'capability' ? 'A computed Capability value is rescaled onto this same 1–20 scale.' : undefined}
        />
      ) : null}

      {subjectType === 'skill' ? (
        <div className={styles.field}>
          <label className={styles.label} htmlFor={`req-proficiency-${roleId}`}>
            Minimum proficiency level
          </label>
          <select
            id={`req-proficiency-${roleId}`}
            className={styles.input}
            value={thresholdProficiencyLevel}
            onChange={(event) =>
              setThresholdProficiencyLevel(event.target.value as 'beginner' | 'intermediate' | 'advanced' | 'expert')
            }
          >
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
            <option value="expert">Expert</option>
          </select>
        </div>
      ) : null}

      {subjectType === 'experience_area' ? (
        <Field
          id={`req-years-${roleId}`}
          label="Minimum years"
          value={thresholdMinYears}
          onChange={setThresholdMinYears}
        />
      ) : null}

      <Field
        id={`req-from-${roleId}`}
        label="Effective from (YYYY-MM-DD)"
        value={effectiveFrom}
        onChange={setEffectiveFrom}
        hint="Periods may not overlap. If this subject is already required, end that period first."
      />
      <ActionFeedback state={state} />
    </AdminForm>
  );
}
